import React, { useEffect, useRef, useState } from 'react';
import api from '../utils/api';
import { imageSrc } from './common/SupplierImages';
import { mobileOverlayStyle } from '../utils/headerOverlay';

import { closeOtherMenus, onCloseMenus } from '../utils/menuEvents';
import { useTranslation } from 'react-i18next';
import { setLanguage, SUPPORTED_LANGUAGES } from '../i18n';
import { translateKnown } from '../i18n/knownMessages';
import { validatePassword, passwordStrength, translatedProblem } from '../utils/passwordPolicy';

const AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

// Initials avatar (no profile photo is stored in the app yet)
const initialsOf = (name = '') =>
  name.split(/\s+|_|-/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('') || '?';

// Clickable username that opens a compact account popover. Shows the logged-in user's own profile only.
export default function AccountMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  // Shown in the header; updated on save so it refreshes without a page reload
  const [displayName, setDisplayName] = useState(user.username);
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const { t, i18n } = useTranslation();
  const [langOpen, setLangOpen] = useState(false); // Language submenu in the profile menu
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [avatar, setAvatar] = useState(user.avatar || null); // stored key; null shows the initials fallback
  // Change Password form inside the popover; the message is shown in place
  const [changingPassword, setChangingPassword] = useState(false);
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwMessage, setPwMessage] = useState(null); // { type: 'success' | 'error', text }
  const [pwSaving, setPwSaving] = useState(false);
  const wrapRef = useRef(null);
  // Opening the profile closes the admin nav and header menus, and they close it in turn
  useEffect(() => {
    if (!open) return undefined;
    closeOtherMenus('profile');
    return onCloseMenus('profile', () => close());
  }, [open]);

  // Load the profile from the server each time the popover opens
  useEffect(() => {
    if (!open) return;
    api.get('/api/users/me')
      .then((res) => { setProfile(res.data); setUsername(res.data.username); setAvatar(res.data.avatar || null); })
      .catch(() => setError('Could not load your profile.'));
  }, [open]);

  // Close on outside click or Escape
  useEffect(() => {
    if (!open) return undefined;
    // While the edit modal is open, outside clicks and Escape do nothing; only Save or Cancel close it
    const busy = editing || changingPassword;
    const onDown = (e) => { if (!busy && wrapRef.current && !wrapRef.current.contains(e.target)) close(); };
    const onKey = (e) => { if (!busy && e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, editing, changingPassword]);

  const close = () => { setOpen(false); setLangOpen(false); setEditing(false); setError(''); setChangingPassword(false); setPwMessage(null); };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwMessage(null);
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setPwMessage({ type: 'error', text: t('profile.mismatch') });
      return;
    }
    const pwProblem = validatePassword(pwForm.newPassword);
    if (pwProblem) {
      setPwMessage({ type: 'error', text: translatedProblem(t, pwProblem) });
      return;
    }
    setPwSaving(true);
    try {
      const res = await api.post('/api/users/me/password', pwForm);
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPwMessage({ type: 'success', text: res.data?.message || 'Password changed successfully.' });
    } catch (err) {
      setPwMessage({ type: 'error', text: err.response?.data?.message || 'Could not change your password.' });
    } finally {
      setPwSaving(false);
    }
  };

  // Client checks mirror the server; the server enforces them again
  const uploadAvatar = async (file) => {
    if (!file) return;
    if (!AVATAR_TYPES.includes(file.type) || file.size > AVATAR_MAX_BYTES) {
      setError('Choose a JPEG, PNG or WebP image up to 5 MB.');
      return;
    }
    const form = new FormData();
    form.append('avatar', file);
    try {
      const res = await api.post('/api/users/me/avatar', form);
      setAvatar(res.data.avatar);
      setError('');
      const stored = JSON.parse(localStorage.getItem('user') || 'null');
      if (stored) localStorage.setItem('user', JSON.stringify({ ...stored, avatar: res.data.avatar }));
    } catch (err) {
      setError(err.response?.data?.message || 'Could not upload your photo.');
    }
  };

  // Immediate UI switch; a signed-in user's choice is also saved to the profile
  const changeLanguage = async (code) => {
    setLangOpen(false);
    setLanguage(code);
    if (!localStorage.getItem('token')) return;
    try {
      await api.patch('/api/users/me', { language: code });
      const stored = JSON.parse(localStorage.getItem('user') || 'null');
      if (stored) localStorage.setItem('user', JSON.stringify({ ...stored, language: code }));
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save your language.');
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await api.patch('/api/users/me', { username });
      setProfile(res.data);
      setDisplayName(res.data.username);
      // Keep the header and stored session in step with the new name
      const stored = JSON.parse(localStorage.getItem('user') || 'null');
      if (stored) localStorage.setItem('user', JSON.stringify({ ...stored, username: res.data.username }));
      close();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save your profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      ref={wrapRef}
      className="relative sm:static inline-block min-w-0 max-w-[26vw] sm:max-w-none ml-3 mr-2"
      // Close the dropdown when focus moves to something outside it (not while editing)
      onBlur={(e) => { if (!editing && !changingPassword && e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) close(); }}
    >
      <button
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="block max-w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap hover:text-green-700 focus:outline-none"
      >
        {avatar && <img src={imageSrc(avatar)} alt="" className="inline h-6 w-6 rounded-full object-cover mr-1 align-middle" />}
        {/* Mobile: the initials circle stands in for the profile icon when there is no photo; desktop keeps its text */}
        {!avatar && <span aria-hidden="true" className="sm:hidden inline-flex h-6 w-6 shrink-0 rounded-full bg-green-600 text-white text-[10px] font-semibold items-center justify-center mr-1 align-middle">{initialsOf(displayName)}</span>}
        <span className="hidden sm:inline">{t('nav.welcome')}</span><strong className="min-w-0 truncate">{displayName}</strong>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t('profile.account')}
          style={mobileOverlayStyle()}
          className="fixed left-4 right-4 top-16 max-h-[calc(100vh-5rem)] overflow-y-auto overflow-x-hidden bg-white text-gray-800 rounded-xl shadow-lg border border-gray-200 py-4 text-sm sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-0 sm:w-72 sm:max-h-none"
        >
          <div className="flex items-center gap-3 px-5 pt-2 pb-4">
            {avatar ? (
              <img src={imageSrc(avatar)} alt="" className="h-12 w-12 shrink-0 rounded-full object-cover" />
            ) : (
              <div className="h-12 w-12 shrink-0 rounded-full bg-green-600 text-white flex items-center justify-center font-semibold">
                {initialsOf(profile?.username || user.username)}
              </div>
            )}
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="min-w-0 flex-1 text-left hover:text-green-700 hover:font-bold focus:outline-none"
              aria-label={t('profile.editAria')}
            >
              <div className="font-semibold truncate">{profile?.username || user.username}</div>
              <div className="text-gray-500 truncate">{profile?.email || ''}</div>
            </button>
          </div>
          <div className="border-t border-gray-200 mx-4" />

          {changingPassword ? (
            <form onSubmit={changePassword} className="space-y-3 px-4 pt-3 pb-2">
              <p className="font-semibold">{t('profile.changePassword')}</p>
              {[
                ['currentPassword', t('profile.currentPassword'), 'current-password'],
                ['newPassword', t('profile.newPassword'), 'new-password'],
                ['confirmPassword', t('profile.confirmPassword'), 'new-password'],
              ].map(([field, label, autoComplete]) => (
                <label key={field} className="block">
                  <span className="block text-xs text-gray-600 mb-1">{label}</span>
                  <input
                    type="password"
                    value={pwForm[field]}
                    onChange={(e) => setPwForm((f) => ({ ...f, [field]: e.target.value }))}
                    className="border p-2 w-full rounded"
                    autoComplete={autoComplete}
                    minLength={field === 'currentPassword' ? undefined : 8}
                    required
                  />
                </label>
              ))}
              <p className="text-xs text-gray-500">{t('password.guidanceProfile')}</p>
              {pwForm.newPassword && (
                <p className="text-xs text-gray-600">
                  {t('password.strength')}: <span className={`font-medium ${{ weak: 'text-red-600', medium: 'text-yellow-600', strong: 'text-green-600' }[passwordStrength(pwForm.newPassword)]}`}>{t('password.' + passwordStrength(pwForm.newPassword))}</span>
                </p>
              )}
              {pwForm.confirmPassword && pwForm.confirmPassword !== pwForm.newPassword && (
                <p className="text-xs text-red-600">{t('password.confirmMismatch')}</p>
              )}
              {pwMessage && (
                <p className={`text-xs ${pwMessage.type === 'success' ? 'text-green-700' : 'text-red-600'}`} role="status">{translateKnown(t, pwMessage.text)}</p>
              )}
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => { setChangingPassword(false); setPwMessage(null); }} className="px-3 py-1 rounded bg-gray-200">{t('profile.back')}</button>
                <button type="submit" disabled={pwSaving} className="px-3 py-1 rounded bg-green-600 text-white disabled:opacity-50">
                  {pwSaving ? t('profile.saving') : t('profile.update')}
                </button>
              </div>
            </form>
          ) : editing ? (
            <form onSubmit={save} className="space-y-3 px-4 pt-3 pb-2">
              <label className="block">
                <span className="block text-xs text-gray-600 mb-1">{t('profile.username')}</span>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="border p-2 w-full rounded"
                  required
                />
              </label>
              <label className="block">
                <span className="block text-xs text-gray-600 mb-1">{t('profile.emailReadOnly')}</span>
                <input type="email" value={profile?.email || ''} readOnly className="border p-2 w-full rounded bg-gray-100 text-gray-500" />
              </label>
              {error && <p className="text-red-600 text-xs">{translateKnown(t, error)}</p>}
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={close} className="px-3 py-1 rounded bg-gray-200">{t('profile.cancel')}</button>
                <button type="submit" disabled={saving} className="px-3 py-1 rounded bg-green-600 text-white disabled:opacity-50">
                  {saving ? t('profile.saving') : t('profile.save')}
                </button>
              </div>
            </form>
          ) : (
            <div className="pt-3 pb-2">
              {error && <p className="text-red-600 text-xs px-4 pb-2">{translateKnown(t, error)}</p>}
              <button type="button" onClick={() => setEditing(true)} className="block w-full text-left px-4 py-2.5 hover:bg-gray-100">
                {t('profile.editProfile')}
              </button>
              <button type="button" onClick={() => { setChangingPassword(true); setPwMessage(null); }} className="block w-full text-left px-4 py-2.5 hover:bg-gray-100">
                {t('profile.changePassword')}
              </button>
              <button type="button" aria-haspopup="menu" aria-expanded={langOpen} onClick={() => setLangOpen(!langOpen)} className="hidden sm:flex w-full items-center justify-between px-4 py-2.5 text-left hover:bg-gray-100">
                <span>{t('profile.language')}</span>
                <span className="text-gray-600">{SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language)?.label}</span>
              </button>
              {langOpen && (
                <div role="menu" aria-label={t('profile.language')} className="hidden sm:block pl-4 pr-2 pb-1">
                  {SUPPORTED_LANGUAGES.map((l) => (
                    <button key={l.code} type="button" role="menuitemradio" aria-checked={i18n.language === l.code} onClick={() => changeLanguage(l.code)} className="flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm hover:bg-gray-100">
                      {l.label}
                      {i18n.language === l.code && <span aria-hidden="true" className="text-green-600">✓</span>}
                    </button>
                  ))}
                </div>
              )}
              <label className="block w-full text-left px-4 py-2.5 hover:bg-gray-100 cursor-pointer">
                {t('profile.changePhoto')}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={(e) => uploadAvatar(e.target.files[0])} />
              </label>
              <div className="border-t border-gray-200 my-1" />
              <a href="/help" className="block w-full px-4 py-2.5 hover:bg-gray-100">
                {t('nav.help')}
              </a>
              <div className="border-t border-gray-200 my-1" />
              <button type="button" onClick={onLogout} className="block w-full text-left px-4 py-2.5 hover:bg-gray-100">
                {t('profile.logout')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
